import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { MessageCircle, Plus, RotateCcw, Send, X } from "lucide-react";
import { toast } from "sonner";
import { api, imgUrl, money } from "@/lib/api";
import { useStream } from "@/lib/useStream";
import { useCart } from "@/context/CartContext";
import { CHAT } from "@/constants/testIds";
import { markChatUsed } from "@/lib/chatAttribution";

const SESSION_KEY = "kiddee_chat_session";
const TAG = /\[\[(bundle:)?([a-z0-9-]+)\]\]/g;
const newSession = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2, 12)}`);
const STARTERS = ["What toy is best for a heavy chewer?", "Do you have treats for training?", "How long does shipping take?"];

// Splits a reply into plain text and recommended product slugs; hides a half-streamed tag.
const parse = (text) => ({
  body: text.replace(TAG, "").replace(/\[\[[a-z0-9:-]*\]?$/, "").replace(/\n{3,}/g, "\n\n").trim(),
  slugs: [...new Set([...text.matchAll(TAG)].filter((m) => !m[1]).map((m) => m[2]))],
  bundles: [...new Set([...text.matchAll(TAG)].filter((m) => m[1]).map((m) => m[2]))],
});

const ProductCard = ({ p, session }) => {
  const { add } = useCart();
  return (
    <div data-testid={CHAT.product(p.slug)} className="flex items-center gap-3 bg-white border border-[#F0EBE1] rounded-2xl p-2">
      <img src={imgUrl(p.image)} alt="" className="w-12 h-12 rounded-xl object-cover" />
      <Link to={`/product/${p.slug}`} className="min-w-0 flex-1 text-sm leading-tight hover:underline">
        <span className="block font-semibold truncate">{p.name}</span>
        <span className="text-stone-500">{money(p.price)}</span>
      </Link>
      <button data-testid={CHAT.productAdd(p.slug)} onClick={() => add(p, 1, { source: "chat", chat_session: session })} aria-label={`Add ${p.name} to cart`} className="w-9 h-9 rounded-full bg-[#E06D44] text-white grid place-items-center hover:bg-[#C95B33] active:scale-95 transition-[background-color,transform]"><Plus size={16} /></button>
    </div>
  );
};

const BundleCard = ({ b, session }) => {
  const { addMany } = useCart();
  return (
    <div data-testid={CHAT.bundle(b.slug)} className="bg-white border-2 border-[#4E7B68]/30 rounded-2xl p-3 space-y-2">
      <div className="flex items-center gap-2">
        <span className="px-2 py-0.5 rounded-full bg-[#4E7B68] text-white text-[10px] font-semibold uppercase tracking-wide">Bundle · save {money(b.saving)}</span>
        <span className="text-sm font-semibold truncate">{b.name}</span>
      </div>
      <div className="flex -space-x-2">{b.products.map((p) => <img key={p.slug} src={imgUrl(p.image)} alt={p.name} title={p.name} className="w-11 h-11 rounded-xl object-cover border-2 border-white" />)}</div>
      <p className="text-xs text-stone-500 leading-snug">{b.products.map((p) => p.name).join(" + ")}</p>
      <div className="flex items-center justify-between gap-2">
        <p className="text-sm"><span data-testid={CHAT.bundlePrice(b.slug)} className="font-bold">{money(b.price)}</span> <span className="text-stone-400 line-through">{money(b.full_price)}</span></p>
        <button data-testid={CHAT.bundleAdd(b.slug)} onClick={() => addMany(b.products, b.name, { source: "chat", chat_session: session })} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-[#E06D44] text-white text-xs font-semibold hover:bg-[#C95B33] active:scale-95 transition-[background-color,transform]"><Plus size={14} /> Add bundle</button>
      </div>
    </div>
  );
};

const Message = ({ m, catalog, bundleMap, session }) => {
  if (m.role === "user") return <p className="self-end max-w-[85%] bg-[#1C1917] text-white text-sm rounded-2xl rounded-br-md px-3.5 py-2 whitespace-pre-line">{m.text}</p>;
  const { body, slugs, bundles } = parse(m.text);
  const products = slugs.map((s) => catalog[s]).filter(Boolean);
  const picks = bundles.map((s) => bundleMap[s]).filter(Boolean);
  return (
    <div className="self-start max-w-[90%] space-y-2">
      {body && <p className="bg-[#F0EBE1] text-sm rounded-2xl rounded-bl-md px-3.5 py-2 whitespace-pre-line">{body}</p>}
      {picks.map((b) => <BundleCard key={b.slug} b={b} session={session} />)}
      {products.map((p) => <ProductCard key={p.slug} p={p} session={session} />)}
    </div>
  );
};

const useChatSession = () => {
  const [session, setSession] = useState(() => localStorage.getItem(SESSION_KEY) || newSession());
  const [messages, setMessages] = useState([]);
  useEffect(() => {
    localStorage.setItem(SESSION_KEY, session);
    api.get(`/chat/${session}`).then(({ data }) => setMessages(data.messages)).catch(() => setMessages([]));
  }, [session]);
  const reset = () => { setMessages([]); setSession(newSession()); };
  return { session, messages, setMessages, reset };
};

const ChatPanel = ({ onClose }) => {
  const { session, messages, setMessages, reset } = useChatSession();
  const [catalog, setCatalog] = useState({});
  const [text, setText] = useState("");
  const { busy, run } = useStream();
  const { items } = useCart();
  const endRef = useRef(null);
  const [bundleMap, setBundleMap] = useState({});
  useEffect(() => { api.get("/products").then(({ data }) => setCatalog(Object.fromEntries(data.map((p) => [p.slug, p])))).catch(() => {}); }, []);
  useEffect(() => { api.get("/bundles").then(({ data }) => setBundleMap(Object.fromEntries(data.map((b) => [b.slug, b])))).catch(() => {}); }, []);
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [messages]);

  const send = async (msg) => {
    const message = msg.trim();
    if (!message || busy) return;
    setText("");
    setMessages((l) => [...l, { role: "user", text: message }, { role: "assistant", text: "" }]);
    const update = (t) => setMessages((l) => [...l.slice(0, -1), { role: "assistant", text: t }]);
    try {
      const cart = items.map(({ slug, qty }) => ({ slug, qty }));
      const reply = await run("/chat", { session_id: session, message, cart }, update);
      if (reply) markChatUsed(session);
      else setMessages((l) => l.slice(0, -1));
    } catch (e) {
      setMessages((l) => l.slice(0, -1));
      toast.error(e.message);
    }
  };

  return (
    <div data-testid={CHAT.panel} className="fixed z-[60] inset-0 h-[100dvh] w-full rounded-none sm:inset-auto sm:bottom-24 sm:right-6 sm:w-[380px] sm:h-[min(560px,calc(100vh-8rem))] sm:rounded-3xl bg-[#FDFBF7] sm:border border-[#E7E0D3] shadow-2xl flex flex-col overflow-hidden fade-up">
      <header className="flex items-center gap-3 px-4 py-3 pt-[max(0.75rem,env(safe-area-inset-top))] sm:pt-3 bg-[#1C1917] text-white">
        <span className="w-9 h-9 rounded-full bg-[#E06D44] grid place-items-center font-heading font-extrabold">K</span>
        <div className="flex-1 leading-tight"><p className="font-semibold">Ask Kiddee</p><p className="text-xs text-white/70">Toy and treat tips from our shop dog</p></div>
        <button data-testid={CHAT.reset} onClick={reset} aria-label="Start a new chat" title="New chat" className="w-10 h-10 rounded-full grid place-items-center hover:bg-white/10"><RotateCcw size={16} /></button>
        <button data-testid={CHAT.close} onClick={onClose} aria-label="Close chat" className="w-10 h-10 rounded-full grid place-items-center hover:bg-white/10"><X size={19} /></button>
      </header>
      <div data-testid={CHAT.messages} className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-3">
        <p className="self-start max-w-[90%] bg-[#F0EBE1] text-sm rounded-2xl rounded-bl-md px-3.5 py-2">Woof! I'm Kiddee. Tell me about your pet and I'll fetch some ideas from the shop.</p>
        {!messages.length && (
          <div className="flex flex-wrap gap-2">
            {STARTERS.map((s, i) => <button key={s} data-testid={CHAT.starter(i)} onClick={() => send(s)} className="text-sm sm:text-xs px-3.5 py-2 sm:px-3 sm:py-1.5 rounded-full border border-[#E7E0D3] bg-white hover:bg-[#F0EBE1] transition-colors">{s}</button>)}
          </div>
        )}
        {messages.map((m, i) => <Message key={i} m={m} catalog={catalog} bundleMap={bundleMap} session={session} />)}
        {busy && !messages[messages.length - 1]?.text && <p data-testid={CHAT.typing} className="self-start text-xs text-stone-500">Kiddee is sniffing around…</p>}
        <div ref={endRef} />
      </div>
      <form onSubmit={(e) => { e.preventDefault(); send(text); }} className="p-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] sm:pb-3 border-t border-[#F0EBE1] bg-white">
        <div className="flex items-center gap-2">
          <input data-testid={CHAT.input} value={text} onChange={(e) => setText(e.target.value)} maxLength={500} enterKeyHint="send" placeholder="Ask about toys, treats, shipping…" className="flex-1 min-w-0 px-4 py-2.5 rounded-full border border-[#E7E0D3] text-sm focus:outline-none focus:ring-2 focus:ring-[#E06D44]/40" />
          <button data-testid={CHAT.send} type="submit" disabled={busy || !text.trim()} aria-label="Send" className="w-11 h-11 shrink-0 rounded-full bg-[#E06D44] text-white grid place-items-center disabled:opacity-40 hover:bg-[#C95B33] transition-colors"><Send size={16} /></button>
        </div>
        <p className="mt-2 text-[11px] text-stone-500 text-center">Kiddee is an AI helper and can make mistakes. Chats are saved to improve the shop.</p>
      </form>
    </div>
  );
};

const usePhoneScrollLock = (locked) => {
  useEffect(() => {
    if (!locked || !window.matchMedia("(max-width: 639px)").matches) return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [locked]);
};

export const KiddeeChat = () => {
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  usePhoneScrollLock(open);
  if (pathname.startsWith("/admin") || pathname.startsWith("/payment")) return null;
  const raised = pathname.startsWith("/product/");  // clear of the phone sticky Add to cart bar
  const hidden = open || pathname === "/cart";  // phones: keep the cart's Check out button unobstructed
  return (
    <>
      {open && <ChatPanel onClose={() => setOpen(false)} />}
      <button
        data-testid={CHAT.launcher}
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "Close Kiddee chat" : "Chat with Kiddee"}
        className={`fixed z-50 ${raised ? "bottom-24" : "bottom-5"} md:bottom-5 right-4 sm:right-6 ${hidden ? "hidden md:flex" : "flex"} items-center justify-center gap-2 w-14 sm:w-auto h-14 sm:pl-4 sm:pr-5 rounded-full bg-[#1C1917] text-white shadow-xl ring-2 ring-white/80 hover:bg-[#E06D44] active:scale-95 transition-[background-color,transform]`}
      >
        {open ? <X size={20} /> : <MessageCircle size={22} />}
        <span className="hidden sm:inline text-sm font-semibold">{open ? "Close" : "Ask Kiddee"}</span>
      </button>
    </>
  );
};
